/**
 * BatchMatrixRowCompact - Compact inline view for Matrix Row
 *
 * Mode-aware: shows template combobox, collection select, or prompt preview.
 */

import { useState } from 'react';
import { ChevronDown, Trash2, Copy, Image as ImageIcon, Upload, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Combobox } from '@/components/v2/ui';
import { cn } from '@/lib/utils';
import type { MatrixItem, BatchMode } from '@/lib/api/batch';

interface Template {
  id: string;
  name: string;
}

interface Collection {
  id: string;
  name: string;
}

interface BatchMatrixRowCompactProps {
  item: MatrixItem;
  index: number;
  templates: Template[];
  collections: Collection[];
  collectionImageCount?: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onChange: (item: MatrixItem) => void;
  onSelectFile: () => void;
  onSelectFromGallery: () => void;
  canRemove: boolean;
  disabled?: boolean;
}

const ASPECT_RATIOS = ['1:1', '16:9', '9:16', '4:3', '3:2'];
const VARIANTS_EXTENDED = [2, 3, 4, 5, 6, 7, 8, 9, 10];

export function BatchMatrixRowCompact({
  item,
  index,
  templates,
  collections,
  collectionImageCount,
  isExpanded,
  onToggleExpand,
  onRemove,
  onDuplicate,
  onChange,
  onSelectFile,
  onSelectFromGallery,
  canRemove,
  disabled = false,
}: BatchMatrixRowCompactProps) {
  const { t } = useTranslation();
  const [showVariantsPopover, setShowVariantsPopover] = useState(false);
  const itemMode: BatchMode = item.mode || 'template';
  const isCollectionMode = itemMode === 'collection';

  const totalImages = isCollectionMode
    ? (collectionImageCount || 1)
    : (item.aspect_ratios?.length || 1) * (item.variants || 1);

  const handleAspectToggle = (ratio: string) => {
    if (disabled) return;
    const current = item.aspect_ratios || ['1:1'];
    const updated = current.includes(ratio) ? current.filter((r) => r !== ratio) : [...current, ratio];
    onChange({ ...item, aspect_ratios: updated.length > 0 ? updated : ['1:1'] });
  };

  const handleVariantChange = (v: number) => {
    if (disabled) return;
    onChange({ ...item, variants: v });
  };

  const templateOptions = templates.map((t) => ({ value: t.id, label: t.name }));
  const collectionOptions = collections.map((c) => ({ value: c.id, label: c.name }));
  const displayedSecondVariant = item.variants > 2 ? item.variants : 2;

  return (
    <div className="relative border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 p-3">
      <div className="flex items-center gap-2">
        {/* Row Number Badge */}
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold">
          {index + 1}
        </div>

        {/* Thumbnail */}
        <div className="flex-shrink-0">
          {item.input_type === 'asset' ? (
            <div className="relative">
              {item._thumbnailUrl ? <img src={item._thumbnailUrl} alt={item.product_name || ''} className="w-16 h-16 rounded object-cover" /> : <div className="w-16 h-16 rounded bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center"><ImageIcon className="h-5 w-5 text-neutral-400" /></div>}
              {item.product_name && <div className="absolute -bottom-1 left-0 right-0 text-2xs text-center truncate bg-black/70 text-white rounded-b px-1">{item.product_name}</div>}
            </div>
          ) : item._thumbnailUrl ? (
            <div className="relative group">
              <img src={item._thumbnailUrl} alt="" className="w-16 h-16 rounded object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center gap-1">
                <button type="button" onClick={onSelectFile} disabled={disabled} className="p-1 rounded bg-white/20 hover:bg-white/40 text-white"><Upload className="h-4 w-4" /></button>
                <button type="button" onClick={onSelectFromGallery} disabled={disabled} className="p-1 rounded bg-white/20 hover:bg-white/40 text-white"><ImageIcon className="h-4 w-4" /></button>
              </div>
            </div>
          ) : (
            <div className="w-16 h-16 rounded border-2 border-dashed border-neutral-300 dark:border-neutral-600 flex items-center justify-center gap-1 text-neutral-400 hover:border-orange-400 hover:text-orange-500 transition-colors">
              <button type="button" onClick={onSelectFile} disabled={disabled} className="p-1"><Upload className="h-4 w-4" /></button>
              <button type="button" onClick={onSelectFromGallery} disabled={disabled} className="p-1"><ImageIcon className="h-4 w-4" /></button>
            </div>
          )}
        </div>

        {/* Mode-aware Content Area */}
        <div className="w-48 shrink-0">
          {itemMode === 'template' && (
            <Combobox placeholder={t('batch.templateChoose')} searchPlaceholder={t('batch.templateSearch')} emptyText={t('batch.noTemplates')} options={templateOptions} value={item.template_id || ''} onValueChange={(v) => onChange({ ...item, template_id: v })} disabled={disabled} />
          )}
          {itemMode === 'collection' && (
            <div className="flex items-center gap-1.5">
              <Combobox placeholder="Collection..." searchPlaceholder="Suchen..." emptyText="Keine Collections" options={collectionOptions} value={item.collection_id || ''} onValueChange={(v) => onChange({ ...item, collection_id: v })} disabled={disabled} />
              {collectionImageCount != null && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-2xs font-medium whitespace-nowrap">
                  <Layers className="h-3 w-3" />{collectionImageCount}
                </span>
              )}
            </div>
          )}
          {itemMode === 'prompt' && (
            <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate px-2 py-1.5 bg-neutral-50 dark:bg-neutral-900 rounded border border-neutral-200 dark:border-neutral-700">
              {item.custom_prompt?.trim() || 'Prompt eingeben...'}
            </div>
          )}
        </div>

        {/* Aspect Ratios + Variants (hidden for collection mode) */}
        {!isCollectionMode && (
          <>
            <div className="flex gap-1">
              {ASPECT_RATIOS.map((ratio) => (
                <button key={ratio} type="button" onClick={() => handleAspectToggle(ratio)} disabled={disabled}
                  className={cn('px-2 py-1 rounded text-xs font-medium transition-colors', (item.aspect_ratios || ['1:1']).includes(ratio) ? 'bg-orange-500 text-white' : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-600', disabled && 'opacity-50 cursor-not-allowed')}>
                  {ratio}
                </button>
              ))}
            </div>
            <div className="flex gap-1 relative">
              <button type="button" onClick={() => handleVariantChange(1)} disabled={disabled} className={cn('w-8 h-8 rounded text-xs font-medium transition-colors', item.variants === 1 ? 'bg-orange-500 text-white' : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-600', disabled && 'opacity-50 cursor-not-allowed')}>1</button>
              <button type="button" onClick={() => handleVariantChange(displayedSecondVariant)} disabled={disabled} className={cn('w-8 h-8 rounded text-xs font-medium transition-colors', item.variants === displayedSecondVariant ? 'bg-orange-500 text-white' : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-600', disabled && 'opacity-50 cursor-not-allowed')}>{displayedSecondVariant}</button>
              <button type="button" onClick={() => setShowVariantsPopover(!showVariantsPopover)} disabled={disabled} className="w-8 h-8 rounded text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-600">+</button>
              {showVariantsPopover && (
                <div className="absolute top-10 left-0 z-50 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-600 rounded-lg shadow-lg p-2 grid grid-cols-3 gap-2 w-[128px]">
                  {VARIANTS_EXTENDED.map((v) => (
                    <button key={v} type="button" onClick={() => { handleVariantChange(v); setShowVariantsPopover(false); }} className={cn('w-8 h-8 rounded text-xs font-medium transition-colors', item.variants === v ? 'bg-orange-500 text-white' : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-orange-400 hover:text-white')}>{v}</button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Total Images Badge */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500 text-white shadow-md">
          <ImageIcon className="h-3.5 w-3.5" />
          <span className="text-xs font-semibold font-mono">{totalImages} {t('common.images')}</span>
        </div>

        <div className="flex-1" />

        {/* Actions */}
        <button type="button" onClick={onToggleExpand} disabled={disabled} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors" aria-label={isExpanded ? 'Einklappen' : 'Ausklappen'}>
          <ChevronDown className={cn('h-4 w-4 text-neutral-500 transition-transform', isExpanded && 'rotate-180')} />
        </button>
        <button type="button" onClick={onDuplicate} disabled={disabled} className="p-1.5 hover:bg-primary-50 dark:hover:bg-primary-950/30 rounded transition-colors" title="Zeile duplizieren">
          <Copy className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        </button>
        <button type="button" onClick={onRemove} disabled={disabled || !canRemove} className={cn('p-1.5 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors', (!canRemove || disabled) && 'opacity-50 cursor-not-allowed')} title="Zeile entfernen">
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </div>
    </div>
  );
}

